import {
  Category,
  useCreateMutation,
  useCurrentWorkspace,
  useWorkspaceCategories,
} from '@gripp/shared-logic';
import {
  LoadingIndicator,
  useSlideLayout,
} from '@gripp/shared-ui';
import { MultiItemSelectorModal } from 'libs/shared-ui/src/components/forms/multiItemSelectorModal';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

const CREATE_CATEGORY = `
  mutation createCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      id
      name
    }
  }
`;

type CategoryItem = { id: string; name: string };

const EditCategory = ({
  selectedCategories,
  onCategoriesChange,
}: {
  selectedCategories: CategoryItem[];
  onCategoriesChange: (categories: CategoryItem[]) => void;
}) => {
  const { t } = useTranslation();
  const { setColumnTitle } = useSlideLayout();
  const currentWorkspace = useCurrentWorkspace();
  const { categories: availableCategories, isLoading } = useWorkspaceCategories();
  const { mutateAsync: createCategory } = useCreateMutation({
    modelName: 'category',
    query: CREATE_CATEGORY,
  });

  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const allCategories = useMemo(() => {
    const combined = [...(availableCategories ?? []), ...selectedCategories];
    const map = new Map<string, CategoryItem>();
    combined.forEach((cat) => {
      if (cat?.id) map.set(cat.id, { id: cat.id, name: cat.name });
    });
    return Array.from(map.values());
  }, [availableCategories, selectedCategories]);

  const selectedCategoryIds = useMemo(
    () => new Set(selectedCategories.map((c) => c.id)),
    [selectedCategories]
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const base = query
      ? allCategories.filter((c) => c.name.toLowerCase().includes(query))
      : allCategories;
    return base.map((c) => ({
      id: c.id,
      name: c.name,
      isActive: selectedCategoryIds.has(c.id),
    }));
  }, [allCategories, search, selectedCategoryIds]);

  const canCreateItem =
    !!search.trim() &&
    !allCategories.some(
      (c) => c.name.toLowerCase() === search.trim().toLowerCase()
    );

  const handleItemToggle = (item: Category) => {
    const isSelected = selectedCategoryIds.has(item.id);
    const next = isSelected
      ? selectedCategories.filter((c) => c.id !== item.id)
      : [...selectedCategories, { id: item.id, name: item.name }];
    onCategoriesChange(next);
  };

  const handleToggleAll = (selectAll: boolean) => {
    let next: CategoryItem[];
    if (selectAll) {
      const toAdd = filteredItems
        .filter((item) => !selectedCategoryIds.has(item.id))
        .map((item) => ({ id: item.id, name: item.name }));
      next = [...selectedCategories, ...toAdd];
    } else {
      const filteredIds = new Set(filteredItems.map((item) => item.id));
      next = selectedCategories.filter((c) => !filteredIds.has(c.id));
    }
    onCategoriesChange(next);
  };

  const handleCreateCategory = async (name: string): Promise<Category> => {
    const result = await createCategory({
      input: { name: name.trim(), workspace: { id: currentWorkspace?.id } },
    });
    return { id: result.id, name };
  };

  const handleCreateItem = async () => {
    if (!search.trim()) return;
    setIsCreating(true);
    try {
      const newCat = await handleCreateCategory(search.trim());
      const next = [...selectedCategories, newCat];
      onCategoriesChange(next);
      setSearch('');
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    setColumnTitle(
      `${t('file.create.category')} (${selectedCategories.length})`
    );
  }, [setColumnTitle, t, selectedCategories.length]);

  if (isLoading) return <LoadingIndicator />;

  return (
    <View style={styles.container}>
      <MultiItemSelectorModal
        showOptions={true}
        search={search}
        setSearch={setSearch}
        searchPlaceholder={t('file.create.searchCategory')}
        filteredItems={filteredItems}
        onItemToggle={handleItemToggle}
        onToggleAll={handleToggleAll}
        canCreateItem={canCreateItem}
        onCreateItem={handleCreateCategory}
        onCreatePress={handleCreateItem}
        isCreating={isCreating}
        createButtonText={t('common.create')}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
});

export default EditCategory;
